import { Box, Button, Checkbox, styled, Tab, Tabs } from '@mui/material';
import { FC, useEffect, useState } from 'react';

import { Notifications, Person2 } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import ButtonGroup from 'components/ButtonGroup';
import Heading from 'components/Heading';
import InteractiveTable, {
  Column,
  useRowsEdit,
} from 'components/InteractiveTable';
import { Row } from 'components/Layout';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import PageSection from 'components/PageSection';
import ReviewPayments from 'components/ReviewPayments';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { ActionOperationType } from 'further-types/lp/action';
import { TransactionOperation } from 'further-types/lp/transaction';
import { usePagination } from 'further-ui/hooks';
import { ActionRefLabels } from 'further-ui/labels';
import {
  CalendarDay,
  numberToCurrencyString,
  pluralize,
} from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import PaymentFiltersAndSummary from '../PaymentFiltersAndSummary';

const StyledTable = styled(InteractiveTable)({
  '& .MuiDataGrid-row--editing': {
    boxShadow: '0 0px 0px rgba(0, 0, 0, 0.15)',
  },
});

enum ApprovePrepaymentsTab {
  ResolvedPayments = 'resolvedPayments',
  ResolvedDeferredIncome = 'resolvedDeferredIncome',
}

const tabsData = {
  [ApprovePrepaymentsTab.ResolvedPayments]: {
    title: 'Resolved payments',
    icon: <Person2 />,
  },
  [ApprovePrepaymentsTab.ResolvedDeferredIncome]: {
    title: 'Resolved deferred income',
    icon: <Notifications />,
  },
};

const pageId = 'approve-prepayments';

const ApprovePrepayments: FC = () => {
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle } = useVehicle(selectedVehicleId);
  const [showUnapprovedOnly, setShowUnapprovedOnly] = useState<boolean>(false);
  const [viewDate, setViewDate] = useState<Date | null>(new Date());
  const [activeTab, setActiveTab] = useState<ApprovePrepaymentsTab>(
    ApprovePrepaymentsTab.ResolvedPayments,
  );
  const [selectedRows, setSelectedRows] = useState<Array<string>>([]);
  const [transactionChanges, setTransactionChanges] = useState<
    Map<
      string,
      Array<{
        transactionTypeId: string;
        operationType: TransactionOperation;
      }>
    >
  >(new Map());
  const pagination = usePagination({
    id: pageId,
    order: 'desc',
    orderBy: 'ledgerDate',
  });
  const { rowsEditModel, editManyRows } = useRowsEdit();

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const { data, isFetching } = api.prepayments.listPrepayments.useQuery({
    vehicleId: selectedVehicleId ?? '',
    showUnapprovedOnly,
    viewDate: viewDate ? new CalendarDay(viewDate) : undefined,
    actionOperationType:
      activeTab === ApprovePrepaymentsTab.ResolvedPayments
        ? ActionOperationType.Spent
        : ActionOperationType.Received,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    order: pagination.order,
    orderBy: pagination.orderBy,
  });

  const cleanupState = () => {
    setSelectedRows([]);
    editManyRows([]);
    setTransactionChanges(new Map());
  };

  const apiRef = useGridApiRef();
  const handleTabChange = (_: unknown, selectedTab: ApprovePrepaymentsTab) => {
    cleanupState();
    setActiveTab(selectedTab);
  };

  const handleTransactionChange = (
    rowId: string,
    newTransactions: Array<{
      transactionTypeId: string;
      operationType: TransactionOperation;
    }>,
  ) => {
    setTransactionChanges((prev) => {
      const newMap = new Map(prev);
      newMap.set(rowId, newTransactions);
      return newMap;
    });

    apiRef.current.updateRows([
      {
        id: rowId,
        transactions: newTransactions,
      },
    ]);
  };

  const mutationOpts = useHandleTrpcMutation();
  const { mutate: approvePrepayments, isPending } =
    api.prepayments.approvePrepayments.useMutation(
      mutationOpts({
        successMessage: 'Prepayments approved successfully',
        onSuccess: () => cleanupState(),
      }),
    );

  const approveTransactions = () => {
    const approvePrepaymentsBody = selectedRows.map((rowId) => {
      const transactions = transactionChanges.get(rowId);
      return {
        actionId: rowId,
        transactions: transactions ?? [],
      };
    });
    approvePrepayments({
      vehicleId: selectedVehicleId ?? '',
      prepayments: approvePrepaymentsBody,
    });
  };

  const rowsForTable = data?.results ?? [];

  useEffect(() => {
    editManyRows(
      rowsForTable
        .filter((row) => !row.isAccountScheduleAuthorised)
        .map((row) => ({ id: row.id })),
    );
  }, [rowsForTable]);

  const columns: Array<Column> = [
    {
      field: 'actionRef',
      headerName: 'Action',
      renderCell: ({ row }) => ActionRefLabels[row.actionRef],
      flex: 1,
      sortable: false,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      sortable: false,
    },
    {
      field: 'ledgerDate',
      headerName: 'Date',
      renderCell: ({ row }) => row.ledgerDate.toLabel(),
      flex: 1,
    },
    {
      field: 'amountTcy',
      headerName: 'Amount (TCY)',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.amountTcy, { fallback: '-' }),
      flex: 1,
      sortable: false,
    },
    {
      field: 'amountLcy',
      headerName: 'Amount (LCY)',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.amountLcy, { fallback: '-' }),
      flex: 1,
      sortable: false,
    },
    {
      field: 'counterpartyLabel',
      headerName: 'Counterparty',
      flex: 1,
      sortable: false,
    },
    {
      field: 'isAccountScheduleAuthorised',
      headerName: 'Approved',
      editable: true,
      align: 'center',
      renderCell: ({ row }) => {
        if (row.isAccountScheduleAuthorised) {
          return 'Yes';
        }
        return 'No';
      },
      renderEditCell: (params) => {
        const rowId = params.row.id!;
        return (
          <Checkbox
            checked={params.value}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedRows((prev) => [...prev, rowId]);
                params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: e.target.checked,
                });
              } else {
                setSelectedRows((prev) => prev.filter((id) => id !== rowId));
                params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: e.target.checked,
                });
              }
            }}
          />
        );
      },
      flex: 1,
      sortable: false,
    },
  ];

  return (
    <PageContainer heading="Automated Transactions: Resolve prepayments">
      <PageContent withSpacing>
        <PaymentFiltersAndSummary
          title="Resolve prepayments"
          showUnapprovedOnly={showUnapprovedOnly}
          viewDate={viewDate}
          onViewDateChange={(date) => {
            cleanupState();
            setViewDate(date);
          }}
          onShowUnapprovedOnlyChange={(value) => {
            cleanupState();
            setShowUnapprovedOnly(value);
          }}
          disableFields={isPending}
        />

        <PageSection withSpacing>
          <Tabs
            showDivider
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            {Object.keys(tabsData).map((tab) => (
              <Tab
                key={tab}
                value={tab}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    {tabsData[tab].icon}
                    <Heading variant="h5" noMargin>
                      {tabsData[tab].title}
                    </Heading>
                  </Box>
                }
              />
            ))}
          </Tabs>
          <StyledTable
            apiRef={apiRef}
            id={pageId}
            columns={columns}
            rows={rowsForTable}
            pagination={pagination}
            loading={isFetching}
            rowsEditModel={rowsEditModel}
            scrollable
            getExpandedContent={(row) => (
              <>
                <Row fullWidth>
                  {!transactionTypesLoading && vehicle && (
                    <TransactionAllocator
                      showCurrencyRate
                      transactionTypeOptions={transactionTypeOptions}
                      transactions={row.transactions}
                      readOnly={row.isAccountScheduleAuthorised}
                      onChange={(transactions) => {
                        handleTransactionChange(row.id ?? '', transactions);
                      }}
                      transactionCurrency={row.currencyCode}
                      vehicle={vehicle}
                      fxRate={null}
                    />
                  )}
                </Row>
                <Row fullWidth>
                  <ReviewPayments
                    transactionId={row.transactionId}
                    vehicleId={selectedVehicleId ?? ''}
                    currencyCode={row.currencyCode}
                    amountTcy={row.amountTcy}
                  />
                </Row>
              </>
            )}
          />

          {selectedRows.length > 0 && (
            <ButtonGroup>
              <Button
                variant="contained"
                color="primary"
                onClick={approveTransactions}
                disabled={isPending}
              >
                Approve {selectedRows.length} automated{' '}
                {pluralize('transaction', selectedRows.length)}
              </Button>
            </ButtonGroup>
          )}
        </PageSection>
      </PageContent>
    </PageContainer>
  );
};

export default ApprovePrepayments;
